import moment from 'moment-timezone';
import { parseLatLonText, toFuelIndex, toKm } from './odAnalyticsUtils';

/**
 * The fleet console's trip export, column for column.
 *
 * The shape the Trip Report used to hand downstream was unusable in a spreadsheet: "2.4 km" put
 * a number and its unit in one cell, " 13 minutes  21 seconds " was duration as padded prose,
 * "null Litre" stapled a unit to a missing reading, and "3.152582, 101.576207" packed both axes
 * into a single quoted field. None of that survives a pivot table and none of it can be summed.
 *
 * Every column here is one fact: numbers are bare, compound fields are split, blanks stay blank,
 * and the figures the old report never carried — peak speed, moving and idle seconds, overspeed,
 * harsh counts, fuel and energy per trip — travel with the row that earned them.
 */
export const TRIP_EXPORT_COLS = [
    'assetName', 'deviceSerial', 'tripNo', 'tripId',
    'startTimeUTC', 'startTimeLocal', 'endTimeUTC', 'endTimeLocal', 'utcOffset',
    'durationHHMMSS',
    'originName', 'originLat', 'originLon', 'destinationName', 'destinationLat', 'destinationLon',
    'distanceKm',
    'peakSpeedKmh', 'avgMovingSpeedKmh',
    'movingSec', 'idleSec', 'excessiveIdleSec', 'idleEvents',
    'overspeedEpisodes', 'overspeedSec', 'overspeedPeakKmh',
    'harshAccel', 'harshBrake', 'harshCorner', 'ecoScore',
    'fuelLitre', 'energyKwh',
];

/**
 * '' rather than 0 for a missing reading — 0 L and "no fuel signal" are different facts, and
 * an eco score of 0 is the *worst* one, so reporting it for a trip that carried none would
 * flag a careful driver as reckless.
 *
 * Nullish is rejected before Number(): Number(null) and Number('') are both 0, which is how a
 * missing reading turns into a confident zero.
 */
const round = (value, dp) => {
    if (value === null || value === undefined || value === '') return '';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '';
    const factor = 10 ** dp;
    return Math.round(parsed * factor) / factor;
};

/** Coordinates need 6 dp (~0.11 m) to match the source; rounding them to 2 flattens a fix to ~1.1 km. */
const coord = (value) => (Number.isFinite(value) ? value.toFixed(6) : '');

/** Reads well, and is the only duration column — a spreadsheet sums movingSec/idleSec instead. */
const hhmmss = (seconds) => {
    // Number(null) and Number('') are both 0, which would report a trip with no duration
    // recorded as having lasted no time at all.
    if (seconds === null || seconds === undefined || seconds === '') return '';
    const total = Number(seconds);
    if (!Number.isFinite(total) || total < 0) return '';
    const whole = Math.round(total);
    return [Math.floor(whole / 3600), Math.floor((whole % 3600) / 60), whole % 60]
        .map((part) => String(part).padStart(2, '0')).join(':');
};

/** Fold analytics rows onto the trip they fell inside. Rows between trips carry no tripId. */
const foldByTrip = (rows, keep, fold) => {
    const byTrip = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        if (!row?.tripId || (keep && !keep(row))) return;
        byTrip.set(row.tripId, fold(byTrip.get(row.tripId), row));
    });
    return byTrip;
};

const countAndSum = (rows, keep) => foldByTrip(rows, keep, (acc, row) => ({
    count: (acc?.count || 0) + 1,
    durationSec: (acc?.durationSec || 0) + (Number(row.durationSec) || 0),
    excessSec: (acc?.excessSec || 0) + (Number(row.excessSec) || 0),
    peakKmh: Math.max(acc?.peakKmh ?? 0, Number(row.peakKmh) || 0),
}));

/**
 * One export row per trip, in TRIP_EXPORT_COLS order.
 *
 * `analytics` is the merged GET /v3/trip/analytics payload. When it is absent — the telemetry
 * engines are flagged off, or the window carried no telemetry — every column that can only come
 * from it is left **blank rather than 0**. A zero here reads as "this trip had no harsh events",
 * which is a measurement the report did not make.
 *
 * `tripNo` restarts at 1 per asset, matching the fleet console: it numbers an asset's own trips,
 * so a two-asset export has two #1s and each reads as that asset's first journey.
 */
export const buildTripExportRows = (trips, options) => {
    const { analytics, timezone } = options || {};
    const zone = timezone || 'UTC';
    const list = (Array.isArray(trips) ? trips : []).filter(Boolean);

    const hasAnalytics = Boolean(analytics?.events || analytics?.episodes || analytics?.speedTrips);
    const fuelByTrip = toFuelIndex(analytics?.fuelTrips);
    const peakByTrip = new Map(
        (Array.isArray(analytics?.speedTrips) ? analytics.speedTrips : [])
            .filter((row) => row?.tripId)
            .map((row) => [row.tripId, Number(row.maxKmh)]),
    );
    const idleByTrip = countAndSum(analytics?.episodes);
    const overspeedByTrip = countAndSum(analytics?.events, (row) => row.type === 'OVERSPEED');
    const accelByTrip = countAndSum(analytics?.events, (row) => row.type === 'HARSH_ACCEL');
    const brakeByTrip = countAndSum(analytics?.events, (row) => row.type === 'HARSH_BRAKE');
    const cornerByTrip = countAndSum(analytics?.events, (row) => row.type === 'HARSH_CORNER');

    // Blank when the payload never loaded, the folded count when it did — see the note above.
    const measured = (source, tripId, pick) => {
        if (!hasAnalytics) return '';
        const found = source.get(tripId);
        return found ? pick(found) : 0;
    };

    const tripNumbers = new Map();

    return list.map((trip) => {
        const tripId = trip.startTraceId ?? '';
        const asset = trip.assetName ?? '';
        const nextNo = (tripNumbers.get(asset) || 0) + 1;
        tripNumbers.set(asset, nextNo);

        const start = trip.startTime ? moment.tz(trip.startTime, zone) : null;
        const end = trip.endTime ? moment.tz(trip.endTime, zone) : null;
        const [originLon, originLat] = parseLatLonText(trip.originGeo) || [];
        const [destLon, destLat] = parseLatLonText(trip.destinationGeo) || [];

        // Engine-on time split by the trip's own idlingRatio, the same exclusion every other view
        // makes: a trip with no ratio contributes to neither half, because an absent signal is
        // unknown idling rather than none.
        const durationSec = Number(trip.totalDuration);
        const ratio = Number(trip.idlingRatio);
        const splitKnown = Number.isFinite(durationSec) && durationSec > 0
            && trip.idlingRatio !== null && trip.idlingRatio !== undefined && trip.idlingRatio !== ''
            && Number.isFinite(ratio);
        const movingSec = splitKnown ? Math.round((1 - ratio) * durationSec) : '';
        const idleSec = splitKnown ? Math.round(ratio * durationSec) : '';

        // Distance is rounded the way the panel rounds it everywhere else, so an exported figure
        // reconciles against the table it was read from rather than differing in the last digit.
        const distanceKm = toKm(trip.totalDistance);
        // The server sends no per-trip average, so it is distance over time actually moving —
        // the same quantity, and it stays blank while the vehicle never moved rather than 0.
        const avgMovingSpeedKmh = movingSec > 0 ? round((distanceKm * 3600) / movingSec, 2) : '';
        const peakKmh = peakByTrip.get(tripId);

        return {
            assetName: asset,
            deviceSerial: trip.deviceSerial ?? '',
            tripNo: nextNo,
            tripId,
            startTimeUTC: start?.isValid() ? start.clone().utc().toISOString() : '',
            startTimeLocal: start?.isValid() ? start.format('YYYY-MM-DD HH:mm:ss') : '',
            endTimeUTC: end?.isValid() ? end.clone().utc().toISOString() : '',
            endTimeLocal: end?.isValid() ? end.format('YYYY-MM-DD HH:mm:ss') : '',
            // Per trip, not per report: an agency on a DST-observing zone changes offset mid-window,
            // and a single label would misdescribe half the rows beneath it.
            utcOffset: start?.isValid() ? start.format('Z') : '',
            durationHHMMSS: hhmmss(trip.totalDuration),
            originName: trip.origin ?? '',
            originLat: coord(originLat),
            originLon: coord(originLon),
            destinationName: trip.destination ?? '',
            destinationLat: coord(destLat),
            destinationLon: coord(destLon),
            distanceKm,
            peakSpeedKmh: Number.isFinite(peakKmh) ? Math.round(peakKmh) : '',
            avgMovingSpeedKmh,
            movingSec,
            idleSec,
            excessiveIdleSec: measured(idleByTrip, tripId, (row) => Math.round(row.excessSec)),
            idleEvents: measured(idleByTrip, tripId, (row) => row.count),
            overspeedEpisodes: measured(overspeedByTrip, tripId, (row) => row.count),
            overspeedSec: measured(overspeedByTrip, tripId, (row) => Math.round(row.durationSec)),
            overspeedPeakKmh: measured(overspeedByTrip, tripId, (row) => Math.round(row.peakKmh)) || '',
            harshAccel: measured(accelByTrip, tripId, (row) => row.count),
            harshBrake: measured(brakeByTrip, tripId, (row) => row.count),
            harshCorner: measured(cornerByTrip, tripId, (row) => row.count),
            ecoScore: round(trip.ecoScore, 2),
            // A CAN asset has no tripfuelLitre — its litres come from the reconciled ledger or not
            // at all, so the ledger's figure wins wherever it exists.
            fuelLitre: round(
                fuelByTrip.get(tripId)?.litres ?? trip.totalFuelLitre ?? trip.tripfuelLitre, 2,
            ),
            energyKwh: round(trip.energyUsedPerTrip, 2),
        };
    });
};

const safeName = (value) => String(value ?? 'asset')
    .replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'asset';

/** 20260726T035759Z — basic ISO 8601, nothing to strip, unambiguous, sorts lexicographically. */
const utcStamp = (isoTime) => {
    const parsed = moment.utc(isoTime);
    return parsed.isValid() ? `${parsed.format('YYYYMMDDTHHmmss')}Z` : 'na';
};

/**
 * Name the file from the data actually exported, not from the picker.
 *
 * Stamped in UTC with a trailing Z. The stamps used to be written in local time with no marker,
 * so a name reading 2026-06-01T10-25-30 sat eight hours away from the startTimeUTC column beside
 * it and nothing said so. UTC also sorts correctly and survives a DST change. Local time is still
 * in the file, in its own labelled column.
 */
export const tripExportFileName = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const assets = [...new Set(list.map((row) => row.assetName).filter(Boolean))];
    const serials = [...new Set(list.map((row) => row.deviceSerial).filter(Boolean))];
    // Both ends of every trip, not just the starts: the window the file covers closes when the
    // last trip ended, and an ongoing trip contributes only the start it has.
    const stamps = list
        .flatMap((row) => [row.startTimeUTC, row.endTimeUTC]).filter(Boolean).sort();

    const who = assets.length === 1 ? safeName(assets[0]) : 'all_assets';
    const imei = assets.length === 1 && serials.length === 1 ? `_${safeName(serials[0])}` : '';

    return `trip_report_${who}${imei}_${utcStamp(stamps[0])}_${utcStamp(stamps[stamps.length - 1])}.csv`;
};
